import { computed } from 'vue';
import { createGtm } from '@gtm-support/vue-gtm';
import useAppSettingStore from '@/store/modules/appSettings.js';
import useLocaleStore from '@/store/modules/locale.js';
import useAuthStore from '@/store/modules/auth.js';
import useThemeHook from '@/hooks/useTheme.js';
import useGtmHook from '@/hooks/useGtm.js';

const useAppSetupHook = (app) => {
  const initialDataFetch = async () => {
    const appSettingsStore = useAppSettingStore();
    const localeStore = useLocaleStore();
    const authStore = useAuthStore();

    const { loadCssFiles } = useThemeHook();

    const promiseActionList = [
      { action: appSettingsStore.dispatchRetrieveAppProperties },
      { action: appSettingsStore.dispatchRetrieveGtmInfo },
      { action: localeStore.dispatchRetrieveTranslations },
    ];

    await localeStore.dispatchRetrieveDefaultLanguage().catch((error) => error);
    await localeStore.dispatchRetrieveLanguages().catch((error) => error);

    if (authStore.isUserAuthenticated) {
      await authStore.dispatchRetrieveAuthUser()
        .then(() => {
          authStore.getPostLoginRequestList().forEach((action) => {
            promiseActionList.push({ action });
          });
        })
        .catch((error) => error);
    }

    await Promise.allSettled(promiseActionList.map(({ action }) => action()));

    await loadCssFiles();

    if (appSettingsStore.isMusicEnabled) {
      appSettingsStore.playMusic();
    }
  };

  const registerGlobalProperties = () => {
    const appSettingsStore = useAppSettingStore();
    const localeStore = useLocaleStore();

    const registerStaticProperties = () => {
      app.config.globalProperties.$_translate = (key) => localeStore.translations[key];

      app.config.globalProperties.$_translateByOperator = (key) =>
        localeStore.translations[`${ key }.${ appSettingsStore.multiOperatorProperties.multiOperatorOrdinal }`] ||
        localeStore.translations[key];
    };

    const registerReactiveProperties = () => {
      app.config.globalProperties.$_translations = computed(() => localeStore.translations);
    };

    registerStaticProperties();

    registerReactiveProperties();
  };

  const initializeGtm = (router) => {
    const appSettingsStore = useAppSettingStore();

    const { loadOldNoScript, loadGoogleAnalyticsScript } = useGtmHook();

    if (!appSettingsStore.gtmKey) { return null; }

    loadOldNoScript();

    loadGoogleAnalyticsScript();

    return createGtm({
      id: appSettingsStore.gtmKey,
      vueRouter: router,
      enabled: true,
      debug: false,
      loadScript: false,
      defer: false,
      compatibility: false,
      nonce: undefined,
    });
  };

  return {
    initialDataFetch,
    registerGlobalProperties,
    initializeGtm,
  };
};

export default useAppSetupHook;

